import React, { useEffect, useState } from 'react';
import Toast from './Toast';
import { parseLinks } from '../utils/parseLinks';
import { ICONS } from '../utils/icons';
import { CheckCircle } from 'lucide-react';
import { buildReceiptMetadata, ReceiptMetadata } from '../utils/receipt';

// Типы для CloudPayments
declare global {
  interface Window {
    cp: {
      CloudPayments: new () => {
        start: (options: {
          publicTerminalId: string;
          description: string;
          amount: number;
          currency: string;
          userInfo: {
            accountId: string;
          };
          accountId: string;
          tokenize: boolean;
          skin: string;
          externalId: string;
          paymentSchema?: string;
          /** Email, на который касса отправит чек */
          receiptEmail?: string;
          /** Чек 54-ФЗ: { JsonData: { CloudPayments: { CustomerReceipt: {...} } } } */
          metadata?: ReceiptMetadata;
        }) => Promise<any>;
      };
    };
  }
}

interface PricingFeature {
  icon?: string;
  text: string;
}

interface Plan {
  name: string;
  price: number;
  periodLabel: string;
  description: string | null;
  features: PricingFeature[];
  checkboxText: string | null;
  popular: boolean;
  gradient: string;
}

interface SiteSettings {
  basic_plan_name: string;
  basic_price: number;
  basic_period_label: string;
  basic_description: string | null;
  basic_features_json: string | null;
  basic_checkbox_text: string | null;

  trial_plan_name: string;
  trial_price: number;
  trial_period_label: string;
  trial_description: string | null;
  trial_features_json: string | null;
  trial_checkbox_text: string | null;

  privacy_checkbox_text: string | null;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FeatureIcon: React.FC<{ name?: string; className?: string }> = ({ name, className }) => {
  if (!name) return <CheckCircle className={className} />;
  const Icon = ICONS[name];
  if (Icon) return <Icon className={className} />;
  return <span className={className}>{name}</span>;
};

const parseFeatures = (json: string | null): PricingFeature[] => {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const Pricing: React.FC = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
  const [agreeToRecurring, setAgreeToRecurring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Состояние для Toast уведомлений
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  useEffect(() => {
    fetch(`${API_URL}/api/site-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setSettings(data as SiteSettings);
      })
      .catch(() => {});
  }, []);

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const handlePlanSelect = (plan: Plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
    setEmail('');
    setEmailError('');
    setAgreeToPrivacy(false);
    setAgreeToRecurring(false);
    setIsLoading(false);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (value && !validateEmail(value)) {
      setEmailError('Введите корректный email адрес');
    } else {
      setEmailError('');
    }
  };

  const handlePurchase = async () => {
    if (!email) {
      setEmailError('Email обязателен для заполнения');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Введите корректный email адрес');
      return;
    }

    if (email && agreeToPrivacy && agreeToRecurring && selectedPlan) {
      setIsLoading(true);

      try {
        const response = await fetch(`${API_URL}/api/payment/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            plan: selectedPlan.name
          })
        });

        const data = await response.json();

        if (data.status === 'success') {
          handleCloseModal();

          const widget = new window.cp.CloudPayments();
          const public_id = process.env.REACT_APP_CLOUDPAYMENTS_PUBLIC_ID || 'pk_test'
          const description = `Тариф "${selectedPlan.name}"`;
          const paymentOptions = {
            publicTerminalId: public_id,
            description: description,
            amount: selectedPlan.price,
            currency: 'RUB',
            userInfo: {
              accountId: email
            },
            accountId: email,
            skin: 'modern',
            tokenize: true,
            externalId: data.payment_id,
            paymentSchema: "Single",
            receiptEmail: email,
            // Чек по 54-ФЗ — касса пробьёт его и отправит на email покупателя
            metadata: buildReceiptMetadata({
              label: description,
              amount: selectedPlan.price,
              email: email
            })
          };

          widget.start(paymentOptions).then(function(widgetResult) {
            if (widgetResult.success) {
              showToast(
                'Оплата прошла успешно!\n\nПроверьте свою почту - мы отправили вам данные для входа в аккаунт.',
                'success'
              );
            }
          }).catch(function(error) {
            console.log('CloudPayments error:', error);
            showToast('Произошла ошибка при оплате. Попробуйте позже.', 'error');
          });
        } else {
          showToast('Произошла ошибка при создании платежа. Попробуйте позже.', 'error');
        }
      } catch (error) {
        console.error('Ошибка при создании платежа:', error);
        showToast('Произошла ошибка при создании платежа. Попробуйте позже.', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const plans: Plan[] = settings
    ? [
        {
          name: settings.trial_plan_name,
          price: settings.trial_price,
          periodLabel: settings.trial_period_label,
          description: settings.trial_description,
          features: parseFeatures(settings.trial_features_json),
          checkboxText: settings.trial_checkbox_text,
          popular: false,
          gradient: 'from-moonlight-600 to-moonlight-700',
        },
        {
          name: settings.basic_plan_name,
          price: settings.basic_price,
          periodLabel: settings.basic_period_label,
          description: settings.basic_description,
          features: parseFeatures(settings.basic_features_json),
          checkboxText: settings.basic_checkbox_text,
          popular: true,
          gradient: 'from-mystic-600 to-mystic-700',
        },
      ]
    : [];

  return (
    <section id="pricing" className="pt-20 pb-64 px-4">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-elegant font-bold text-white mb-6">
            Выберите свой
            <span className="bg-gradient-to-r from-mystic-400 to-moonlight-400 bg-clip-text text-transparent font-elegant"> тариф</span>
          </h2>
          <p className="text-xl text-lunar-300 max-w-3xl mx-auto font-elegant">
            Справедливые цены за мощные возможности. Начните с пробного тарифа или сразу получите полный доступ
          </p>
        </div>

        {!settings ? (
          <div className="flex justify-center">
            <div className="w-8 h-8 border-2 border-mystic-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-lunar-800/50 backdrop-blur-sm rounded-2xl p-8 border transition-all duration-300 hover:transform hover:scale-105 ${
                  plan.popular
                    ? 'border-[var(--primary-color-500)] shadow-xl shadow-[var(--primary-color-500-25)]'
                    : 'border-[var(--primary-color-800-30)] hover:border-[var(--primary-color-600-50)]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-mystic-gradient px-6 py-2 rounded-full text-white text-sm font-semibold">
                      Популярный
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  {plan.description && <p className="text-lunar-400 mb-6">{plan.description}</p>}

                  <div className="mb-6">
                    <span className="text-5xl font-bold text-white">{plan.price}</span>
                    <span className="text-lunar-400 ml-2">₽ {plan.periodLabel}</span>
                  </div>
                </div>

                {plan.features.length > 0 && (
                  <div className="mb-8">
                    <ul className="space-y-4">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-lunar-300">
                          <FeatureIcon
                            name={feature.icon}
                            className="w-5 h-5 text-mystic-400 mr-3 flex-shrink-0"
                          />
                          <span>{feature.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  onClick={() => handlePlanSelect(plan)}
                  className={`w-full py-4 rounded-lg font-semibold text-white transition-all duration-300 ${
                    plan.popular
                      ? 'bg-mystic-gradient hover:shadow-lg hover:shadow-mystic-500/25'
                      : 'bg-gradient-to-r from-moonlight-600 to-moonlight-700 hover:shadow-lg hover:shadow-moonlight-500/25'
                  }`}
                >
                  Выбрать план
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Purchase Modal */}
        {isModalOpen && selectedPlan && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-lunar-900 rounded-2xl p-8 max-w-md w-full border border-[var(--primary-color-800-30)] relative">
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 text-lunar-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Покупка тарифа "{selectedPlan.name}"
                </h3>
                <p className="text-lunar-400">
                  {selectedPlan.price}₽ {selectedPlan.periodLabel}
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-white text-sm font-medium mb-2">
                  Электронная почта
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="you@example.com"
                  className={`w-full px-4 py-3 bg-lunar-800 border rounded-lg text-white placeholder-lunar-400 focus:outline-none transition-colors ${
                    emailError
                      ? 'border-red-500 focus:border-red-400'
                      : 'border-[var(--primary-color-800-30)] focus:border-[var(--primary-color-500)]'
                  }`}
                />
                {emailError && (
                  <p className="text-red-400 text-sm mt-2">{emailError}</p>
                )}
              </div>

              <div className="space-y-4 mb-6">
                {settings?.privacy_checkbox_text && (
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={agreeToPrivacy}
                        onChange={(e) => setAgreeToPrivacy(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        agreeToPrivacy
                          ? 'bg-mystic-600 border-mystic-600'
                          : 'border-[var(--primary-color-800-50)] bg-lunar-800'
                      }`}>
                        {agreeToPrivacy && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-lunar-300 leading-relaxed">
                      {parseLinks(settings.privacy_checkbox_text)}
                    </span>
                  </label>
                )}

                {selectedPlan.checkboxText && (
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={agreeToRecurring}
                        onChange={(e) => setAgreeToRecurring(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        agreeToRecurring
                          ? 'bg-mystic-600 border-mystic-600'
                          : 'border-[var(--primary-color-800-50)] bg-lunar-800'
                      }`}>
                        {agreeToRecurring && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-lunar-300 leading-relaxed">
                      {parseLinks(selectedPlan.checkboxText)}
                    </span>
                  </label>
                )}
              </div>

              <button
                onClick={handlePurchase}
                disabled={
                  !email ||
                  (!!settings?.privacy_checkbox_text && !agreeToPrivacy) ||
                  (!!selectedPlan.checkboxText && !agreeToRecurring) ||
                  isLoading ||
                  emailError !== ''
                }
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                  email &&
                  (!settings?.privacy_checkbox_text || agreeToPrivacy) &&
                  (!selectedPlan.checkboxText || agreeToRecurring) &&
                  !isLoading &&
                  emailError === ''
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-lunar-700 text-lunar-400 cursor-not-allowed'
                }`}
              >
                {isLoading ? 'Создание платежа...' : `Оплатить ${selectedPlan.price}₽`}
              </button>
            </div>
          </div>
        )}

      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </section>
  );
};

export default Pricing;
